import React from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { SUPPORTED_LANGS } from "../../i18n";

/**
 * Language switcher — used in the Header (desktop + mobile drawer).
 * Persists the user's choice to localStorage via i18next-browser-languagedetector.
 *
 * Variants:
 *   - "compact" (default): icon + current flag, opens a small dropdown.
 *   - "inline":            full row, used inside the mobile drawer.
 */
export default function LanguageSwitcher({ variant = "compact", testIdPrefix = "lang" }) {
  const { i18n } = useTranslation();
  const current = SUPPORTED_LANGS.find((l) => l.code === i18n.resolvedLanguage) || SUPPORTED_LANGS[0];

  const change = (code) => {
    i18n.changeLanguage(code);
  };

  if (variant === "inline") {
    return (
      <div className="flex items-center gap-2 px-5 py-3 border-b hairline" data-testid={`${testIdPrefix}-inline`}>
        <Globe className="w-4 h-4 text-[#666]" strokeWidth={1.75} />
        {SUPPORTED_LANGS.map((l) => (
          <button
            key={l.code}
            onClick={() => change(l.code)}
            className={`text-xs mono uppercase tracking-wider px-2 py-1 border hairline ${
              l.code === current.code ? "bg-[#ff4500] text-white border-[#ff4500]" : "bg-white hover:bg-[#fff4ee]"
            }`}
            data-testid={`${testIdPrefix}-inline-${l.code}`}
            aria-pressed={l.code === current.code}
          >
            {l.flag} {l.code.toUpperCase()}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative group" data-testid={`${testIdPrefix}-compact`}>
      <button
        className="inline-flex items-center justify-center gap-1 w-9 h-9 border hairline hover:bg-[#fff4ee] hover:border-[#ff4500] transition-colors text-xs"
        aria-label="Change language"
        data-testid={`${testIdPrefix}-button`}
      >
        <span className="text-base leading-none">{current.flag}</span>
      </button>
      <div
        className="absolute right-0 top-full mt-1 bg-white border hairline shadow-[0_4px_12px_rgba(0,0,0,0.08)] hidden group-hover:block group-focus-within:block min-w-[140px] z-50"
        data-testid={`${testIdPrefix}-menu`}
      >
        {SUPPORTED_LANGS.map((l) => (
          <button
            key={l.code}
            onClick={() => change(l.code)}
            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[#fff4ee] ${
              l.code === current.code ? "bg-[#fff4ee] text-[#ff4500]" : ""
            }`}
            data-testid={`${testIdPrefix}-option-${l.code}`}
          >
            <span className="text-base leading-none">{l.flag}</span>
            <span>{l.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
